import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { ThemeToggle } from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';

// ── ATI Scale questions ────────────────────────────────────────
const ATI_QUESTIONS = [
  'I like to occupy myself in greater detail with technical systems.',
  'I like testing the functions of new technical systems.',
  'I predominantly deal with technical systems because I have to.',
  'When I have a new technical system in front of me, I try it out intensively.',
  'I enjoy spending time becoming acquainted with a new technical system.',
  'It is enough for me that a technical system works; I don\'t care how or why.',
  'I try to understand how a technical system exactly works.',
  'It is enough for me to know the basic functions of a technical system.',
  'I try to make full use of the capabilities of a technical system.',
];

const ATI_LABELS = ['Completely Disagree', 'Largely Disagree', 'Slightly Disagree', 'Slightly Agree', 'Largely Agree', 'Completely Agree'];

// ── Use-case rows ────────────────────────────────────────────────
const USE_CASES = [
  { key: 'research', label: 'Research about topics of interest' },
  { key: 'learnSkills', label: 'Learn new skills' },
  { key: 'improveWriting', label: 'Improve my writing' },
  { key: 'ideaGeneration', label: 'Idea Generation' },
  { key: 'analyzeData', label: 'Analyse Data' },
  { key: 'analyzeSensitive', label: 'Analyse sensitive documents such as CVs or contracts' },
];

const USE_CASE_OPTIONS = ['Yes', 'No', 'Unsure', "I don't know what Generative AI is", 'No Answer'];

const LEARNING_MATERIALS = [
  "I'm not interested to learn more",
  'Blogs and casual articles',
  'Newsletter with updates',
  'Video explainers',
  'Audio briefings and explainers',
  'Academic Papers',
  'Books',
];

// ── Section card wrapper ─────────────────────────────────────────
function SectionCard({ title, description, children }) {
  return (
    <div className="bg-white dark:bg-surface-dark rounded-xl border border-border-light dark:border-border shadow-sm overflow-hidden mb-4">
      {(title || description) && (
        <div className="px-6 py-4 border-b border-border-light dark:border-border">
          {title && <h2 className="text-base font-semibold text-text-primary-light dark:text-text-primary">{title}</h2>}
          {description && <p className="text-sm text-text-muted-light dark:text-text-muted mt-1">{description}</p>}
        </div>
      )}
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ── Scale radio (1–5 or 0–10) with end labels ────────────────────
function ScaleRadio({ name, min, max, value, onChange, leftLabel, rightLabel }) {
  const steps = [];
  for (let i = min; i <= max; i++) steps.push(i);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[340px]">
        <thead>
          <tr>
            <td className="w-24 pb-1 pr-2 text-xs text-text-muted-light dark:text-text-muted text-right align-bottom">{leftLabel}</td>
            {steps.map((v) => (
              <td key={v} className="text-center text-xs text-text-muted-light dark:text-text-muted pb-1 px-1">{v}</td>
            ))}
            <td className="w-24 pb-1 pl-2 text-xs text-text-muted-light dark:text-text-muted text-left align-bottom">{rightLabel}</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td />
            {steps.map((v) => (
              <td key={v} className="text-center px-1 py-1">
                <input
                  type="radio"
                  name={name}
                  value={v}
                  checked={value === v}
                  onChange={() => onChange(v)}
                  className="w-4 h-4 accent-[#64cc9c] cursor-pointer"
                />
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── ATI Likert grid (6 columns) ──────────────────────────────────
function LikertGrid({ values, onChange }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px]">
        <thead>
          <tr>
            <th className="text-left text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 pr-4 w-64" />
            {ATI_LABELS.map((l) => (
              <th key={l} className="text-center text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 px-2 min-w-[72px]">{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ATI_QUESTIONS.map((q, qi) => (
            <tr key={qi} className={qi % 2 === 0 ? 'bg-background-light dark:bg-background' : ''}>
              <td className="text-sm text-text-secondary-light dark:text-text-secondary py-3 pr-4">{q}</td>
              {[1, 2, 3, 4, 5, 6].map((v) => (
                <td key={v} className="text-center py-3 px-2">
                  <input
                    type="radio"
                    name={`ati_q${qi + 1}`}
                    value={v}
                    checked={values[`q${qi + 1}`] === v}
                    onChange={() => onChange(`q${qi + 1}`, v)}
                    className="w-4 h-4 accent-[#64cc9c] cursor-pointer"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Use-case grid ────────────────────────────────────────────────
function UseCaseGrid({ values, onChange }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px]">
        <thead>
          <tr>
            <th className="text-left text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 pr-4 w-52" />
            {USE_CASE_OPTIONS.map((opt) => (
              <th key={opt} className="text-center text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 px-2 min-w-[72px]">{opt}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {USE_CASES.map((uc, i) => (
            <tr key={uc.key} className={i % 2 === 0 ? 'bg-background-light dark:bg-background' : ''}>
              <td className="text-sm text-text-secondary-light dark:text-text-secondary py-3 pr-4">{uc.label}</td>
              {USE_CASE_OPTIONS.map((opt) => (
                <td key={opt} className="text-center py-3 px-2">
                  <input
                    type="radio"
                    name={`useCase_${uc.key}`}
                    value={opt}
                    checked={values[uc.key] === opt}
                    onChange={() => onChange(uc.key, opt)}
                    className="w-4 h-4 accent-[#64cc9c] cursor-pointer"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Radio group ──────────────────────────────────────────────────
function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="space-y-2 mt-2">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-3 cursor-pointer group">
          <input
            type="radio"
            name={name}
            value={opt}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className="w-4 h-4 accent-[#64cc9c] cursor-pointer flex-shrink-0"
          />
          <span className="text-sm text-text-secondary-light dark:text-text-secondary group-hover:text-text-primary-light dark:group-hover:text-text-primary">{opt}</span>
        </label>
      ))}
    </div>
  );
}

// ── Main SurveyPage ──────────────────────────────────────────────
export default function SurveyPage() {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Form state
  const [aiKnowledge, setAiKnowledge] = useState(null);
  const [ati, setAti] = useState({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null, q7: null, q8: null, q9: null });
  const [fear, setFear] = useState(null);
  const [trust, setTrust] = useState(null);
  const [destroyHumankind, setDestroyHumankind] = useState(null);
  const [benefitHumankind, setBenefitHumankind] = useState(null);
  const [jobLosses, setJobLosses] = useState(null);
  const [jobOpportunities, setJobOpportunities] = useState(null);
  const [awareOfGenAI, setAwareOfGenAI] = useState(null);
  const [genAIFrequency, setGenAIFrequency] = useState(null);
  const [usageContext, setUsageContext] = useState(null);
  const [useCase, setUseCase] = useState({ research: null, learnSkills: null, improveWriting: null, ideaGeneration: null, analyzeData: null, analyzeSensitive: null });
  const [interestedInLearning, setInterestedInLearning] = useState(null);
  const [learningMaterial, setLearningMaterial] = useState([]);
  const [learningMaterialOther, setLearningMaterialOther] = useState('');
  const [gender, setGender] = useState(null);
  const [age, setAge] = useState(null);

  // Default to light mode on load
  const { theme, toggleTheme } = useTheme();
  useEffect(() => {
    if (theme === 'dark') toggleTheme();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dynamic mode state
  const [schema, setSchema] = useState(null);
  const [dynamicFormData, setDynamicFormData] = useState({});
  const [respondentId, setRespondentId] = useState(null);

  useEffect(() => {
    api.get(`/surveys/${surveyId}`)
      .then((r) => {
        setSurvey(r.data);
        // Prefer the schema embedded on the survey (side-aware for paired DBs).
        // Fall back to /databases/:id for legacy payloads that don't include it.
        const embeddedSchema = r.data.schema;
        const applySchema = (sch) => {
          setSchema(sch);
          const initial = {};
          for (const col of sch.columns || []) {
            if (col.type === 'likert-group') {
              initial[col.key] = {};
              for (const sf of col.subFields || []) initial[col.key][sf.key] = null;
            } else if (col.type === 'multi-enum') {
              initial[col.key] = [];
            } else {
              initial[col.key] = null;
            }
          }
          setDynamicFormData(initial);
        };
        if (embeddedSchema) {
          applySchema(embeddedSchema);
        } else if (r.data.databaseId) {
          api.get(`/databases/${r.data.databaseId}`)
            .then((res) => applySchema(res.data))
            .catch(() => { /* schema fetch failed — fall back to legacy */ });
        }
      })
      .catch(() => setError('Survey not found.'))
      .finally(() => setLoading(false));
  }, [surveyId]);

  // Cookie-based matching: generate/retrieve a stable UUID for the session
  useEffect(() => {
    if (!survey || !schema || schema.matchingStrategy === 'manual') return;
    const storageKey = 'prism_rid_' + (survey.pairGroupId || surveyId);
    let rid = localStorage.getItem(storageKey);
    if (!rid) {
      rid = crypto.randomUUID();
      localStorage.setItem(storageKey, rid);
    }
    setRespondentId(rid);
  }, [survey, schema, surveyId]);

  const toggleLearningMaterial = (val) => {
    setLearningMaterial((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const validate = () => {
    const errs = {};
    if (aiKnowledge === null) errs.aiKnowledge = true;
    if (Object.values(ati).some((v) => v === null)) errs.ati = true;
    if (fear === null) errs.fear = true;
    if (trust === null) errs.trust = true;
    if (destroyHumankind === null) errs.destroyHumankind = true;
    if (benefitHumankind === null) errs.benefitHumankind = true;
    if (jobLosses === null) errs.jobLosses = true;
    if (jobOpportunities === null) errs.jobOpportunities = true;
    if (!awareOfGenAI) errs.awareOfGenAI = true;
    if (!genAIFrequency) errs.genAIFrequency = true;
    if (!usageContext) errs.usageContext = true;
    if (Object.values(useCase).some((v) => v === null)) errs.useCase = true;
    if (!interestedInLearning) errs.interestedInLearning = true;
    if (!gender) errs.gender = true;
    if (!age) errs.age = true;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      const firstErr = document.querySelector('[data-error="true"]');
      firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setValidationErrors({});
    setSubmitting(true);
    setSubmitError(null);

    const materials = [...learningMaterial];
    if (learningMaterialOther.trim()) materials.push(learningMaterialOther.trim());

    try {
      await api.post(`/surveys/${surveyId}/respond`, {
        aiKnowledge,
        ati,
        fear,
        trust,
        destroyHumankind,
        benefitHumankind,
        jobLosses,
        jobOpportunities,
        awareOfGenAI,
        genAIFrequency,
        usageContext,
        useCase,
        interestedInLearning,
        learningMaterial: materials,
        gender,
        age,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Dynamic mode helpers ──────────────────────────────────────
  const isDynamic = !!schema && !!survey?.databaseId;

  const setDynField = (key, value) => {
    setDynamicFormData((prev) => ({ ...prev, [key]: value }));
  };

  const setDynSubField = (colKey, subKey, value) => {
    setDynamicFormData((prev) => ({
      ...prev,
      [colKey]: { ...(prev[colKey] || {}), [subKey]: value },
    }));
  };

  const toggleDynMulti = (colKey, value) => {
    setDynamicFormData((prev) => {
      const arr = prev[colKey] || [];
      return { ...prev, [colKey]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const validateDynamic = () => {
    const errs = {};
    for (const col of schema?.columns || []) {
      if (!col.required) continue;
      const val = dynamicFormData[col.key];
      if (col.type === 'likert-group') {
        if (!val || Object.values(val).some((v) => v === null || v === undefined)) errs[col.key] = true;
      } else if (col.type === 'multi-enum') {
        // multi-enum not typically required
      } else if (val === null || val === undefined || val === '') {
        errs[col.key] = true;
      }
    }
    return errs;
  };

  const handleDynamicSubmit = async (e) => {
    e.preventDefault();
    const errs = validateDynamic();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      const firstErr = document.querySelector('[data-error="true"]');
      firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setValidationErrors({});
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = { ...dynamicFormData };
      if (schema?.matchingStrategy !== 'manual' && respondentId) {
        payload._respondentId = respondentId;
      }
      await api.post(`/surveys/${surveyId}/respond`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Group schema columns by surveySection
  const dynamicSections = (() => {
    if (!schema) return [];
    const map = new Map();
    for (const col of schema.columns || []) {
      const section = col.surveySection || 'General';
      if (!map.has(section)) map.set(section, []);
      map.get(section).push(col);
    }
    return Array.from(map, ([title, columns]) => ({ title, columns }));
  })();

  const renderDynamicColumn = (col) => {
    const val = dynamicFormData[col.key];
    const label = col.surveyLabel || col.label;
    const reqMark = col.required ? <span className="text-red-500"> *</span> : null;

    switch (col.type) {
      case 'number':
      case 'scale': {
        const min = col.min ?? 1;
        const max = col.max ?? 5;
        return (
          <div key={col.key} data-error={!!validationErrors[col.key]} className={`border-l-4 pl-1 mb-6 last:mb-0 ${errBorder(col.key)}`}>
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">{label}{reqMark}</p>
            <ScaleRadio
              name={col.key}
              min={min}
              max={max}
              value={val}
              onChange={(v) => setDynField(col.key, v)}
              leftLabel={col.surveyLeftLabel || String(min)}
              rightLabel={col.surveyRightLabel || String(max)}
            />
            {errLabel(col.key)}
          </div>
        );
      }
      case 'likert-group': {
        const subFields = col.subFields || [];
        const labels = (col.likertOptions || []).map((o) => o.label || o);
        const values = (col.likertOptions || []).map((o) => o.value ?? o);
        return (
          <div key={col.key} data-error={!!validationErrors[col.key]} className={`border-l-4 pl-1 mb-6 last:mb-0 ${errBorder(col.key)}`}>
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">{label}{reqMark}</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 pr-4 w-64" />
                    {labels.map((l) => (
                      <th key={l} className="text-center text-xs font-normal text-text-muted-light dark:text-text-muted pb-2 px-2 min-w-[72px]">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subFields.map((sf, i) => (
                    <tr key={sf.key} className={i % 2 === 0 ? 'bg-background-light dark:bg-background' : ''}>
                      <td className="text-sm text-text-secondary-light dark:text-text-secondary py-3 pr-4">{sf.label}</td>
                      {values.map((v) => (
                        <td key={v} className="text-center py-3 px-2">
                          <input
                            type="radio"
                            name={`${col.key}_${sf.key}`}
                            value={v}
                            checked={(val || {})[sf.key] === v}
                            onChange={() => setDynSubField(col.key, sf.key, v)}
                            className="w-4 h-4 accent-[#64cc9c] cursor-pointer"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errLabel(col.key)}
          </div>
        );
      }
      case 'boolean':
        return (
          <div key={col.key} data-error={!!validationErrors[col.key]} className={`border-l-4 pl-1 mb-6 last:mb-0 ${errBorder(col.key)}`}>
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">{label}{reqMark}</p>
            <RadioGroup name={col.key} options={['Yes', 'No']} value={val === true ? 'Yes' : val === false ? 'No' : null} onChange={(v) => setDynField(col.key, v === 'Yes')} />
            {errLabel(col.key)}
          </div>
        );
      case 'enum':
        return (
          <div key={col.key} data-error={!!validationErrors[col.key]} className={`border-l-4 pl-1 mb-6 last:mb-0 ${errBorder(col.key)}`}>
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">{label}{reqMark}</p>
            <RadioGroup name={col.key} options={col.options || []} value={val} onChange={(v) => setDynField(col.key, v)} />
            {errLabel(col.key)}
          </div>
        );
      case 'multi-enum':
        return (
          <div key={col.key} className="mb-6 last:mb-0">
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">{label}</p>
            <div className="space-y-2">
              {(col.options || []).map((opt) => (
                <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={(val || []).includes(opt)}
                    onChange={() => toggleDynMulti(col.key, opt)}
                    className="w-4 h-4 accent-[#64cc9c] cursor-pointer flex-shrink-0 rounded"
                  />
                  <span className="text-sm text-text-secondary-light dark:text-text-secondary group-hover:text-text-primary-light dark:group-hover:text-text-primary">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 'text':
        return (
          <div key={col.key} className="mb-6 last:mb-0">
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-2">{label}{reqMark}</p>
            <textarea
              value={val || ''}
              onChange={(e) => setDynField(col.key, e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
            {errLabel(col.key)}
          </div>
        );
      default:
        return null;
    }
  };

  const errBorder = (key) => validationErrors[key] ? 'border-red-400' : 'border-transparent';
  const errLabel = (key) => validationErrors[key]
    ? <p className="text-xs text-red-500 mt-1">Please answer this question.</p>
    : null;

  const ThemeToggleCorner = () => (
    <div className="fixed top-4 right-4 z-50">
      <ThemeToggle />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex items-center justify-center">
        <ThemeToggleCorner />
        <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex items-center justify-center px-4">
        <ThemeToggleCorner />
        <div className="text-center max-w-md">
          <p className="text-text-muted-light dark:text-text-muted text-lg">{error || 'Survey not found.'}</p>
        </div>
      </div>
    );
  }

  if (!survey.isOpen) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex items-center justify-center px-4">
        <ThemeToggleCorner />
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-border-light dark:border-border shadow-sm p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-background-light dark:bg-background flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-text-muted-light dark:text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary mb-2">Survey Closed</h2>
          <p className="text-text-muted-light dark:text-text-muted text-sm">This survey is no longer accepting responses.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex items-center justify-center px-4">
        <ThemeToggleCorner />
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-border-light dark:border-border shadow-sm p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary mb-2">Thank you!</h2>
          <p className="text-text-muted-light dark:text-text-muted text-sm">Your response has been submitted successfully. We appreciate your time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background py-8 px-4">
      <ThemeToggleCorner />
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="bg-brand-primary rounded-t-xl px-6 py-4">
          <p className="text-white/80 text-xs font-medium uppercase tracking-wider"><b>SeyAI</b> · {survey.companyName}</p>
        </div>
        <div className="bg-white dark:bg-surface-dark border-x border-b border-border-light dark:border-border rounded-b-xl shadow-sm mb-4 px-6 py-5">
          <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary mb-2">{survey.title || 'Awareness of Artificial Intelligence'}</h1>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
            {survey.description ||
              'This questionnaire investigates the awareness of Artificial Intelligence (AI) and Emerging Technology. The aim is to understand how individuals feel about AI, its prospects and its impact on their work. This information is processed and used by SeyAI (SeyTravel Ltd) for research and product development, in an effort to tailor its services.'}
          </p>
          <p className="text-xs text-text-muted-light dark:text-text-muted mt-3">The survey takes approximately 4 minutes to complete. All fields marked with * are required.</p>
        </div>

        <form onSubmit={isDynamic ? handleDynamicSubmit : handleSubmit} noValidate>

          {isDynamic ? (
            /* ── Dynamic survey from schema ──────────────── */
            <>
              {dynamicSections.map((section) => (
                <SectionCard key={section.title} title={section.title}>
                  {section.columns.map(renderDynamicColumn)}
                </SectionCard>
              ))}
            </>
          ) : (
            /* ── Legacy hardcoded AI Literacy survey ─────── */
            <>

          {/* Section 1: AI Knowledge */}
          <SectionCard>
            <div data-error={!!validationErrors.aiKnowledge} className={`border-l-4 pl-1 ${errBorder('aiKnowledge')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">
                How knowledgeable do you consider yourself to be in Artificial Intelligence (AI)? <span className="text-red-500">*</span>
              </p>
              <ScaleRadio
                name="aiKnowledge"
                min={1} max={5}
                value={aiKnowledge}
                onChange={setAiKnowledge}
                leftLabel="I do not know what AI is"
                rightLabel="I am highly proficient in AI"
              />
              {errLabel('aiKnowledge')}
            </div>
          </SectionCard>

          {/* Section 2: ATI Scale */}
          <SectionCard
            title="Affinity for Technology Interaction (ATI) Scale"
            description='This question is intended to understand your interaction with technical systems. The term "technical systems" refers to apps and other software applications, as well as entire digital devices (e.g., mobile phone, computer, TV, car navigation). This scale was proposed by Franke, Attig, & Wessel (2019)'
          >
            <div data-error={!!validationErrors.ati} className={`border-l-4 pl-1 ${errBorder('ati')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">
                Rate each statement: <span className="text-red-500">*</span>
              </p>
              <LikertGrid values={ati} onChange={(key, val) => setAti((prev) => ({ ...prev, [key]: val }))} />
              {errLabel('ati')}
            </div>
          </SectionCard>

          {/* Section 3: Attitude Towards AI */}
          <SectionCard
            title="Attitude Towards Artificial Intelligence"
            description="This is a scale based on the work of Sindermann et al., 2022 to study the attitude toward AI."
          >
            {[
              { label: 'I <strong>fear</strong> Artificial Intelligence.', key: 'fear', val: fear, set: setFear },
              { label: 'I <strong>trust</strong> Artificial Intelligence.', key: 'trust', val: trust, set: setTrust },
              { label: 'Artificial intelligence will <strong>destroy</strong> humankind.', key: 'destroyHumankind', val: destroyHumankind, set: setDestroyHumankind },
              { label: 'Artificial intelligence will <strong>benefit</strong> humankind.', key: 'benefitHumankind', val: benefitHumankind, set: setBenefitHumankind },
              { label: 'Artificial intelligence will <strong>cause many job losses</strong>.', key: 'jobLosses', val: jobLosses, set: setJobLosses },
              { label: 'Artificial intelligence will <strong>create new job opportunities</strong>.', key: 'jobOpportunities', val: jobOpportunities, set: setJobOpportunities },
            ].map(({ label, key, val, set }) => (
              <div key={key} data-error={!!validationErrors[key]} className={`border-l-4 pl-1 mb-6 last:mb-0 ${errBorder(key)}`}>
                <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3" dangerouslySetInnerHTML={{ __html: label + ' <span class="text-red-500">*</span>' }} />
                <ScaleRadio
                  name={key}
                  min={0} max={10}
                  value={val}
                  onChange={set}
                  leftLabel="Strongly Disagree"
                  rightLabel="Strongly Agree"
                />
                {errLabel(key)}
              </div>
            ))}
          </SectionCard>

          {/* Section 4: Usage of AI */}
          <SectionCard title="Usage of Artificial Intelligence">
            {/* Awareness */}
            <div data-error={!!validationErrors.awareOfGenAI} className={`border-l-4 pl-1 mb-6 ${errBorder('awareOfGenAI')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                Are you aware of ChatGPT, Gemini, Claude or other generative AI products? <span className="text-red-500">*</span>
              </p>
              <RadioGroup name="awareOfGenAI" options={['Yes', 'No', 'Unsure']} value={awareOfGenAI} onChange={setAwareOfGenAI} />
              {errLabel('awareOfGenAI')}
            </div>

            {/* Frequency */}
            <div data-error={!!validationErrors.genAIFrequency} className={`border-l-4 pl-1 mb-6 ${errBorder('genAIFrequency')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                How often do you use Generative AI products? <span className="text-red-500">*</span>
              </p>
              <RadioGroup name="genAIFrequency" options={['Daily', 'Weekly', 'Monthly', 'Never']} value={genAIFrequency} onChange={setGenAIFrequency} />
              {errLabel('genAIFrequency')}
            </div>

            {/* Usage context */}
            <div data-error={!!validationErrors.usageContext} className={`border-l-4 pl-1 mb-6 ${errBorder('usageContext')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                For which aspects of life would/do you use ChatGPT, Claude, Gemini or similar generative AI products to assist you? <span className="text-red-500">*</span>
              </p>
              <RadioGroup
                name="usageContext"
                options={['Work', 'Personal', 'Both', "I don't use it", "I don't know what that is."]}
                value={usageContext}
                onChange={setUsageContext}
              />
              {errLabel('usageContext')}
            </div>

            {/* Use cases grid */}
            <div data-error={!!validationErrors.useCase} className={`border-l-4 pl-1 ${errBorder('useCase')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">
                How would you use ChatGPT or other similar generative AI products at work? <span className="text-red-500">*</span>
              </p>
              <UseCaseGrid
                values={useCase}
                onChange={(key, val) => setUseCase((prev) => ({ ...prev, [key]: val }))}
              />
              {errLabel('useCase')}
            </div>
          </SectionCard>

          {/* Section 5: Learning */}
          <SectionCard>
            {/* Interested in learning */}
            <div data-error={!!validationErrors.interestedInLearning} className={`border-l-4 pl-1 mb-6 ${errBorder('interestedInLearning')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                Given the opportunity, would you be interested in furthering your knowledge and expertise in AI? <span className="text-red-500">*</span>
              </p>
              <RadioGroup
                name="interestedInLearning"
                options={[
                  'Yes, through formal education (Diplomas or degrees)',
                  'Yes, through a non-formal programme (Workshops, training sessions)',
                  'No',
                  'Unsure',
                ]}
                value={interestedInLearning}
                onChange={setInterestedInLearning}
              />
              {errLabel('interestedInLearning')}
            </div>

            {/* Learning material */}
            <div>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-3">
                Which material would you be interested in consuming to learn more about AI?
              </p>
              <div className="space-y-2">
                {LEARNING_MATERIALS.map((m) => (
                  <label key={m} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={learningMaterial.includes(m)}
                      onChange={() => toggleLearningMaterial(m)}
                      className="w-4 h-4 accent-[#64cc9c] cursor-pointer flex-shrink-0 rounded"
                    />
                    <span className="text-sm text-text-secondary-light dark:text-text-secondary group-hover:text-text-primary-light dark:group-hover:text-text-primary">{m}</span>
                  </label>
                ))}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={learningMaterialOther !== '' || learningMaterial.includes('__other')}
                    onChange={() => {}}
                    className="w-4 h-4 accent-[#64cc9c] cursor-pointer flex-shrink-0 rounded mt-0.5"
                    onClick={() => learningMaterialOther === '' && setLearningMaterialOther(' ')}
                  />
                  <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                    Other:{' '}
                    <input
                      type="text"
                      value={learningMaterialOther.trim()}
                      onChange={(e) => setLearningMaterialOther(e.target.value)}
                      placeholder="Please specify"
                      className="border-b border-border-light dark:border-border focus:border-brand-primary outline-none text-sm px-1 w-48 bg-transparent text-text-primary-light dark:text-text-primary"
                    />
                  </span>
                </label>
              </div>
            </div>
          </SectionCard>

          {/* Section 6: Demographics */}
          <SectionCard title="Demographics">
            {/* Gender */}
            <div data-error={!!validationErrors.gender} className={`border-l-4 pl-1 mb-6 ${errBorder('gender')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                Gender <span className="text-red-500">*</span>
              </p>
              <RadioGroup
                name="gender"
                options={['Female', 'Male', 'Other', 'Prefer not to say']}
                value={gender}
                onChange={setGender}
              />
              {errLabel('gender')}
            </div>

            {/* Age */}
            <div data-error={!!validationErrors.age} className={`border-l-4 pl-1 ${errBorder('age')}`}>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                Age <span className="text-red-500">*</span>
              </p>
              <RadioGroup
                name="age"
                options={['18-25', '26-35', '36-45', '46-55', '56-65', '65+']}
                value={age}
                onChange={setAge}
              />
              {errLabel('age')}
            </div>
          </SectionCard>

            </>
          )}

          {/* Validation summary */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-6 py-4 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">Please answer all required questions before submitting.</p>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-6 py-4 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end mb-10">
            <button
              type="submit"
              disabled={submitting}
              className="bg-brand-primary hover:opacity-90 disabled:opacity-50 text-white font-semibold px-8 py-3 rounded-xl transition text-sm"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>

          <p className="text-xs text-text-muted-light dark:text-text-muted text-center mb-8">
            Powered by{' '}
            <a
              href="https://ai.seytravel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary hover:opacity-70 transition font-medium"
            >
              SeyAI
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
